import { Component, HostListener, signal, inject, PLATFORM_ID } from '@angular/core';
import { RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import { Icon } from './icon';
import { SearchService } from './search.service';
import { environment } from '../environments/environment';

const NAV = [
  { path: '/', label: 'Home', exact: true },
  { path: '/tours', label: 'Tours', exact: false },
  { path: '/attractions', label: 'Attractions', exact: false },
  { path: '/ayurveda', label: 'Wellness', exact: false },
  { path: '/gallery', label: 'Gallery', exact: false },
  { path: '/about', label: 'Our Story', exact: false },
];

@Component({
  selector: 'app-header',
  imports: [RouterLink, RouterLinkActive, Icon],
  template: `
    <header class="fixed top-0 left-0 right-0 z-[100] transition-all duration-500" [class.py-3]="isScrolled() || isMenuOpen()" [class.py-6]="!isScrolled() && !isMenuOpen()">
      <div class="container">
        <div [class]="barClass()">
          <a routerLink="/" (click)="closeMenu()" class="flex items-center group relative z-[110]">
            <img
              src="/logo.png"
              alt="Step Into Sri Lanka"
              class="h-8 md:h-10 w-auto transition-transform duration-500 group-hover:scale-105"
              [class.brightness-0]="isMenuOpen()"
              [class.invert]="isMenuOpen()"
              referrerpolicy="no-referrer"
            />
          </a>

          <nav class="hidden lg:flex items-center gap-1 glass rounded-full px-2 py-1.5">
            @for (item of nav; track item.path) {
              <a
                [routerLink]="item.path"
                routerLinkActive="text-vermilion"
                [routerLinkActiveOptions]="{ exact: item.exact }"
                class="px-5 py-2 text-[11px] uppercase tracking-[0.18em] font-bold text-ink/70 hover:text-ink transition-colors"
              >
                {{ item.label }}
              </a>
            }
          </nav>

          <div class="flex items-center gap-2 sm:gap-3 relative z-[110]">
            <button type="button" (click)="searchSvc.open()" [class]="searchBtnClass()">
              <app-icon name="search" [size]="15" />
              <span class="hidden sm:inline">Search</span>
              <kbd class="hidden sm:inline text-[9px] opacity-50 font-mono ml-1">&#8984;K</kbd>
            </button>

            <a routerLink="/contact" (click)="closeMenu()" [class]="ctaClass()"> Plan My Trip </a>

            <button
              (click)="toggleMenu()"
              aria-label="Toggle menu"
              [attr.aria-expanded]="isMenuOpen()"
              [class]="menuBtnClass()"
            >
              <app-icon [name]="isMenuOpen() ? 'close' : 'menu'" [size]="20" />
            </button>
          </div>
        </div>
      </div>
    </header>

    <div
      class="grain fixed inset-0 z-[90] glass-ink transition-all duration-700 ease-in-out overflow-hidden"
      [class.opacity-0]="!isMenuOpen()"
      [class.pointer-events-none]="!isMenuOpen()"
      [class.translate-y-0]="isMenuOpen()"
      [class.-translate-y-full]="!isMenuOpen()"
    >
      <div class="mesh-blob mesh-blob-a w-[420px] h-[420px] -top-32 -right-32 bg-saffron/25"></div>
      <div class="mesh-blob mesh-blob-b w-[320px] h-[320px] bottom-0 -left-24 bg-vermilion/20"></div>

      <div class="container h-full flex flex-col justify-center items-start gap-10 pt-24 pb-16 relative z-10">
        <nav class="flex flex-col items-start gap-3 w-full">
          @for (item of nav; track item.path; let i = $index) {
            <a
              [routerLink]="item.path"
              (click)="closeMenu()"
              routerLinkActive="text-saffron"
              [routerLinkActiveOptions]="{ exact: item.exact }"
              class="text-5xl sm:text-6xl font-serif text-paper hover:text-saffron transition-colors flex items-baseline gap-4"
            >
              <span class="route-code text-sm text-saffron/70">0{{ i + 1 }}</span>
              {{ item.label }}
            </a>
          }
        </nav>

        <div class="flex flex-col sm:flex-row gap-4 w-full sm:w-auto pt-4">
          <a routerLink="/contact" (click)="closeMenu()" class="btn-saffron justify-center"> Plan My Trip </a>
          <button type="button" (click)="closeMenu(); searchSvc.open()" class="btn-outline-light justify-center">
            <app-icon name="search" [size]="16" /> Search the Island
          </button>
        </div>

        <div class="mt-auto pt-10 w-full flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-paper/10">
          <p class="text-[10px] uppercase tracking-[0.25em] font-bold text-paper/30 pt-6">Step Into Sri Lanka</p>
          <a [href]="'mailto:' + contactEmail" class="text-paper/60 hover:text-saffron text-sm transition-colors pt-6 sm:pt-0">{{ contactEmail }}</a>
        </div>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class Header {
  private router = inject(Router);
  private platformId = inject(PLATFORM_ID);
  searchSvc = inject(SearchService);
  contactEmail = environment.contactEmail;

  nav = NAV;
  isScrolled = signal(false);
  isMenuOpen = signal(false);

  barClass() {
    const base = 'flex items-center justify-between transition-all duration-500 rounded-full';
    if (this.isMenuOpen()) return `${base} px-2`;
    if (this.isScrolled()) return `${base} glass px-4 py-2 shadow-lg`;
    return `${base} px-2`;
  }

  searchBtnClass() {
    const base =
      'flex items-center gap-2 px-3.5 sm:px-4 py-2 text-[11px] uppercase tracking-[0.15em] font-bold rounded-full border transition-all hover:border-vermilion';
    return this.isMenuOpen()
      ? `${base} border-paper/30 text-paper bg-paper/5`
      : `${base} border-sand/70 text-ink/70 bg-white/30 backdrop-blur-sm`;
  }

  ctaClass() {
    return this.isMenuOpen()
      ? 'hidden sm:inline-flex btn-vermilion'
      : 'hidden sm:inline-flex btn-saffron';
  }

  menuBtnClass() {
    const base =
      'lg:hidden w-10 h-10 flex items-center justify-center rounded-full relative overflow-hidden transition-colors';
    return this.isMenuOpen() ? `${base} text-paper` : `${base} text-ink`;
  }

  constructor() {
    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
      this.closeMenu();
    });
  }

  @HostListener('window:scroll', [])
  onWindowScroll() {
    if (isPlatformBrowser(this.platformId)) {
      this.isScrolled.set(window.scrollY > 40);
    }
  }

  toggleMenu() {
    this.isMenuOpen.update((v) => !v);
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = this.isMenuOpen() ? 'hidden' : '';
    }
  }

  closeMenu() {
    this.isMenuOpen.set(false);
    if (isPlatformBrowser(this.platformId)) {
      document.body.style.overflow = '';
    }
  }
}
